import { ref } from 'vue';
import { WS_URL } from '@/config';
import ChatNotificationService from '@/services/ChatNotificationService';

// Definiere einen Typ für die Message-Handler-Funktion
type MessageHandler = (data: any) => void;

// Singleton-Pattern für eine zentrale WebSocket-Verbindung
class WebSocketService {
  private static instance: WebSocketService;
  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private isConnecting = false;
  private messageHandlers: Map<string, MessageHandler[]> = new Map();

  // Status-Tracking
  public isConnected = ref(false);
  public connectionAttempts = ref(0);
  public lastError = ref('');

  // Private Constructor für Singleton-Pattern
  private constructor() {
    // Leerer Konstruktor für Singleton-Pattern
  }

  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  /**
   * Verbindet per Same-Origin-/ws-Proxy; Auth laeuft ueber HttpOnly-Cookie (kein JWT in der URL).
   */
  public connect(_tokenOverride?: string): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    if (this.isConnecting) return;
    this.isConnecting = true;

    try {
      this.socket = new WebSocket(WS_URL);

      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('Fehler beim Erstellen der WebSocket-Verbindung:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  public connectWithRetry(maxAttempts = 3): Promise<boolean> {
    return new Promise((resolve) => {
      let attempts = 0;

      const attemptConnect = () => {
        attempts++;
        this.connect();

        // Warte auf Verbindung
        setTimeout(() => {
          if (this.isConnected.value) {
            resolve(true);
            return;
          }

          if (attempts < maxAttempts) {
            // Warte etwas länger vor dem nächsten Versuch
            setTimeout(attemptConnect, 500 * attempts);
          } else {
            resolve(false);
          }
        }, 300);
      };

      attemptConnect();
    });
  }

  private handleOpen(): void {
    this.isConnected.value = true;
    this.isConnecting = false;
    this.connectionAttempts.value = 0;
    this.lastError.value = '';

    // Start regelmäßiger Ping-Nachrichten
    this.startPingTimer();
  }

  private handleMessage(event: MessageEvent): void {
    // Strings und JSON-Nachrichten separat behandeln
    if (typeof event.data === 'string') {
      // Einfachen Text behandeln (non-JSON)
      if (event.data === 'pong' || event.data === 'ping') {
        // Bei ping mit pong antworten
        if (event.data === 'ping') {
          this.socket?.send('pong');
        }
        return;
      }

      // Versuche als JSON zu parsen
      try {
        const data = JSON.parse(event.data);

        // Ping/Pong-Nachrichten behandeln
        if (data.type === 'ping') {
          this.send({ type: 'pong', timestamp: Date.now() });
          return;
        }

        // Chat-Nachricht an den ChatNotificationService weiterleiten
        if (data.type === 'chat_message' && !data.self_sent) {
          // Prüfen, ob wir uns im Chat-Bereich befinden
          const isChatActive = window.location.pathname.includes('/chat');

          // Benachrichtigung nur anzeigen, wenn nicht im aktiven Chat
          if (!isChatActive) {
            ChatNotificationService.newMessage(data, false);
          }
        }

        // Benutzerdefinierte Handler ausführen
        this.executeHandlers(data.type, data);
      } catch (error) {
        console.warn('Nicht-JSON-Nachricht empfangen:', event.data);
      }
    } else {
      console.warn('Nicht-Text-Nachricht empfangen:', event.data);
    }
  }

  private handleClose(event: CloseEvent): void {
    this.isConnected.value = false;
    this.isConnecting = false;
    this.socket = null;

    // Ping-Timer stoppen
    this.stopPingTimer();

    // Verbindung neu herstellen
    this.scheduleReconnect();
  }

  private handleError(event: Event): void {
    console.error('WebSocket-Fehler:', event);
    this.lastError.value = 'Verbindungsfehler';
    this.isConnecting = false;
  }

  private scheduleReconnect(): void {
    // Exponentielles Backoff für Wiederverbindungen
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
    }

    const delay = Math.min(30000, Math.pow(1.5, this.connectionAttempts.value) * 1000);
    this.connectionAttempts.value++;

    this.reconnectTimer = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  private startPingTimer(): void {
    this.stopPingTimer();

    // Alle 30 Sekunden einen Ping senden
    this.pingTimer = window.setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping', timestamp: Date.now() });
      }
    }, 30000);
  }

  private stopPingTimer(): void {
    if (this.pingTimer) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  public send(data: any): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      // Wenn data bereits ein String ist, direkt senden (z.B. "update", "ping", "pong")
      // Sonst als JSON stringifyen
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.socket.send(message);
      return true;
    } catch (error) {
      console.error('Fehler beim Senden der WebSocket-Nachricht:', error);
      return false;
    }
  }

  public addMessageHandler(type: string, handler: MessageHandler): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }

    const handlers = this.messageHandlers.get(type)!;

    // Prüfe ob dieser Handler bereits registriert ist (verhindert Duplikate)
    if (!handlers.includes(handler)) {
      handlers.push(handler);
    }
  }

  // Neue Methode: Alle Handler eines Typs entfernen
  public clearMessageHandlers(type: string): void {
    if (this.messageHandlers.has(type)) {
      this.messageHandlers.delete(type);
    }
  }

  public removeMessageHandler(type: string, handler: MessageHandler): void {
    if (!this.messageHandlers.has(type)) return;

    const handlers = this.messageHandlers.get(type) || [];
    const index = handlers.indexOf(handler);

    if (index !== -1) {
      handlers.splice(index, 1);
    }

    if (handlers.length === 0) {
      this.messageHandlers.delete(type);
    }
  }

  private executeHandlers(type: string, data: any): void {
    const handlers = this.messageHandlers.get(type) || [];

    for (const handler of handlers) {
      try {
        handler(data);
      } catch (error) {
        console.error(`Fehler im Handler für Nachrichtentyp "${type}":`, error);
      }
    }
  }

  public disconnect(): void {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopPingTimer();

    if (this.socket) {
      this.socket.onclose = null; // Verhindere automatische Wiederverbindung
      this.socket.close();
      this.socket = null;
    }

    this.isConnected.value = false;
    this.isConnecting = false;
  }

  public cleanup(): void {
    this.disconnect();
    this.messageHandlers.clear();
  }
}

export default WebSocketService.getInstance();
